// packages/ui/src/components/RecoveryDialog.tsx
import { useState, type ReactElement } from 'react';
import { Modal, type ModalAction } from './Modal';

export interface RecoveryItem {
  id: string;
  name: string;
  path: string;
}
export type RecoveryChoice = 'restore' | 'skip';

interface Props {
  open: boolean;
  items: RecoveryItem[];
  onResolve: (decisions: Record<string, RecoveryChoice>) => void;
}

/**
 * Per-file recovery picker for file-backed documents whose unsaved draft
 * differs from the current disk content. Restore keeps the unsaved draft (the
 * doc stays dirty); Skip loads the disk version and discards the draft.
 */
export function RecoveryDialog({ open, items, onResolve }: Props): ReactElement {
  const [choices, setChoices] = useState<Record<string, RecoveryChoice>>(
    () => Object.fromEntries(items.map((i) => [i.id, 'restore' as RecoveryChoice])),
  );
  const set = (id: string, c: RecoveryChoice) => setChoices((s) => ({ ...s, [id]: c }));
  const setAll = (c: RecoveryChoice) =>
    setChoices(Object.fromEntries(items.map((i) => [i.id, c])));

  // Continue commits the current per-file choices and is the affirmative default;
  // Esc/backdrop also commits via onClose (current choices). "Skip all" / "Restore
  // all" are bulk shortcuts that only adjust the choices, not dismiss.
  const actions: ModalAction[] = [
    { id: 'skip-all', label: 'Skip all', onSelect: () => setAll('skip'), accelerator: 's' },
    { id: 'restore-all', label: 'Restore all', onSelect: () => setAll('restore'), accelerator: 'r' },
    { id: 'continue', label: 'Continue', onSelect: () => onResolve(choices), accelerator: 'c', variant: 'primary', isDefault: true },
  ];

  return (
    <Modal
      open={open}
      onClose={() => onResolve(choices)}
      title="Recover unsaved changes?"
      actions={actions}
    >
      <p className="mp-modal__text">
        These files have unsaved changes from your last session that differ from what is on disk.
        Restore keeps your unsaved version; Skip loads the saved file.
      </p>
      <ul className="mp-recovery-list">
        {items.map((it) => (
          <li key={it.id} className="mp-recovery-row">
            <span className="mp-recovery-name" title={it.path}>
              {it.name}
            </span>
            <span className="mp-recovery-actions">
              <button
                type="button"
                aria-label={`Restore ${it.name}`}
                aria-pressed={choices[it.id] === 'restore'}
                data-active={choices[it.id] === 'restore' || undefined}
                className="mp-btn mp-btn--sm"
                onClick={() => set(it.id, 'restore')}
              >
                Restore
              </button>
              <button
                type="button"
                aria-label={`Skip ${it.name}`}
                aria-pressed={choices[it.id] === 'skip'}
                data-active={choices[it.id] === 'skip' || undefined}
                className="mp-btn mp-btn--sm"
                onClick={() => set(it.id, 'skip')}
              >
                Skip
              </button>
            </span>
          </li>
        ))}
      </ul>
    </Modal>
  );
}
